/**
 * Figure de triangle NOMMÉ partagée par les automatismes 3e 1242 (cercle
 * circonscrit du triangle rectangle), 1243 (égalité de Pythagore) et 1244
 * (droite des milieux). Modèle de gen_1078 (5e) : coordonnées abstraites
 * (y vers le haut), cadre ajusté sur les points d'appui, codages portant
 * data-code pour les solveurs, data-figure avec les points nommés.
 *
 * scene = {
 *   points : { A: [x, y], ... }          (tous les points nommés, dessinés avec leur étiquette)
 *   segments : [['A', 'B'], ...]         (traits pleins)
 *   pointilles : [['M', 'N'], ...]        (traits pointillés, ex. la droite des milieux)
 *   angleDroit : 'A' | null              (petit carré au sommet, entre ses deux voisins du triangle)
 *   triangle : ['A', 'B', 'C']           (les trois sommets, pour l'angle droit et les étiquettes)
 *   ticks : [{ de: 'A', a: 'M', n: 1 }, ...]   (codage d'égalité, n traits)
 *   cercle : { centre: 'O', par: 'A' } | null  (cercle de centre O passant par A ; O nommé)
 *   sansEtiquette : ['O']                 (points marqués sans lettre)
 *   cotes : [{ de: 'E', a: 'R', texte: '9 cm', cherche }]   (longueur écrite au milieu du
 *           côté, vers l'extérieur du triangle, à l'horizontale ; « cherche » : en orange)
 *   angles : [{ sommet: 'V', texte: '35°', cherche }]       (arc au sommet, entre ses deux
 *           voisins du triangle, mesure sur la bissectrice ; « cherche » : en orange)
 *   vertes : [['M', 'N'], ['B', 'C']]     (traits pleins verts : les droites parallèles
 *           DONNÉES d'une configuration de Thalès, comme sur les fiches du prof)
 *   carres : [{ sommet: 'E', voisins: ['T', 'R'] }]   (angles droits en plus, entre deux
 *           voisins donnés : figures composées de plusieurs triangles, 189 et 190)
 *   (angles[i].voisins : les deux côtés de l'angle, quand la figure a plus de trois sommets ;
 *    triangle : tous les sommets de la figure, leur centre repousse les écritures)
 *   directions : { O: [dx, dy] }         (le côté où écrire le nom d'un point, en coordonnées
 *           de la scène, y vers le haut : le centre d'un quadrilatère, entre ses diagonales, 190)
 * }
 *
 * Cotes et angles (2026-09-24, vagues 4e Pythagore, Thalès, cosinus) : la
 * figure est EXACTE (proportions et angles vrais) à une échelle libre, les
 * longueurs sont écrites dessus ; le cadre s'élargit pour qu'aucune écriture
 * ne sorte. Sans cotes ni angles, le rendu est celui d'avant, au caractère près.
 */

const W = 320; const H = 240; const MARGE = 30;
const ENCRE = '#1E3A8A';
const COULEUR_COTE = '#0369A1';
const COULEUR_CHERCHE = '#EA580C';
const HAUTEUR_TEXTE = 13;
/** Largeur approchée d'une écriture en Arial 13 px (chiffres 7,3 ; espace, virgule 3,6 ; m 10,8 ; ° 5,2). */
const largeurTexte = (t) => [...String(t)].reduce((s, c) => s + (c === ' ' || c === ',' || c === '.' ? 3.6 : c === 'm' ? 10.8 : c === '°' ? 5.2 : 7.3), 0);
const f1 = (v) => v.toFixed(1);
const vec = (P, Q) => [Q[0] - P[0], Q[1] - P[1]];
const norme = (u) => Math.hypot(u[0], u[1]);
const unitaire = (u) => { const L = norme(u) || 1; return [u[0] / L, u[1] / L]; };
const perp = (u) => [-u[1], u[0]];
export const milieu = (P, Q) => [(P[0] + Q[0]) / 2, (P[1] + Q[1]) / 2];
export const tourner = (pts, deg) => {
    const a = (deg * Math.PI) / 180; const co = Math.cos(a); const si = Math.sin(a);
    return pts.map(([x, y]) => [x * co - y * si, x * si + y * co]);
};

const cadreDe = (appuis, marge = MARGE) => {
    const xs = appuis.map((p) => p[0]); const ys = appuis.map((p) => p[1]);
    const xmin = Math.min(...xs); const xmax = Math.max(...xs);
    const ymin = Math.min(...ys); const ymax = Math.max(...ys);
    const ech = Math.min((W - 2 * marge) / Math.max(xmax - xmin, 1e-6), (H - 2 * marge) / Math.max(ymax - ymin, 1e-6));
    const X = (x) => marge + (x - xmin) * ech + ((W - 2 * marge) - (xmax - xmin) * ech) / 2;
    const Y = (y) => marge + (ymax - y) * ech + ((H - 2 * marge) - (ymax - ymin) * ech) / 2;
    return { ech, P: (p) => [X(p[0]), Y(p[1])] };
};

/** Une longueur écrite au milieu de [PQ], du côté opposé à G, sans toucher le trait : centre et boîte. */
const placeCote = (P, Q, G, texte) => {
    const M = milieu(P, Q); let n = perp(unitaire(vec(P, Q)));
    if ((M[0] - G[0]) * n[0] + (M[1] - G[1]) * n[1] < 0) n = [-n[0], -n[1]];
    const w = largeurTexte(texte); const h = HAUTEUR_TEXTE;
    const d = 5 + Math.abs(n[0]) * (w / 2) + Math.abs(n[1]) * (h / 2);
    return { c: [M[0] + n[0] * d, M[1] + n[1] * d], w, h };
};

/** L'arc d'un angle au sommet S entre les directions vers A et B, et la place de sa mesure sur la bissectrice. */
const placeAngle = (S, A, B, texte) => {
    const ua = unitaire(vec(S, A)); const ub = unitaire(vec(S, B));
    const cosA = Math.max(-1, Math.min(1, ua[0] * ub[0] + ua[1] * ub[1]));
    const demi = Math.acos(cosA) / 2;
    const r = demi < 0.3 ? 30 : 22;
    let bis = [ua[0] + ub[0], ua[1] + ub[1]];
    bis = norme(bis) < 1e-9 ? perp(ua) : unitaire(bis);
    const w = largeurTexte(texte); const h = HAUTEUR_TEXTE;
    // la mesure tient entre les deux côtés : sa demi-diagonale sous la demi-ouverture
    const d = Math.max(r + 6 + Math.hypot(w, h) / 2, (Math.hypot(w, h) / 2 + 3) / Math.sin(Math.max(demi, 0.05)));
    const a0 = [S[0] + ua[0] * r, S[1] + ua[1] * r]; const b0 = [S[0] + ub[0] * r, S[1] + ub[1] * r];
    const sweep = ua[0] * ub[1] - ua[1] * ub[0] > 0 ? 1 : 0;
    return { arc: `M ${f1(a0[0])} ${f1(a0[1])} A ${r} ${r} 0 0 ${sweep} ${f1(b0[0])} ${f1(b0[1])}`, c: [S[0] + bis[0] * d, S[1] + bis[1] * d], w, h };
};

const svgSegment = (P, Q, dash = false) =>
    `<line x1="${f1(P[0])}" y1="${f1(P[1])}" x2="${f1(Q[0])}" y2="${f1(Q[1])}" stroke="${ENCRE}" stroke-width="${dash ? 1.6 : 2.2}"${dash ? ' stroke-dasharray="6 4"' : ''}/>`;

const svgTicks = (P, Q, n) => {
    const m = milieu(P, Q); const u = unitaire(vec(P, Q)); const nv = perp(u);
    let s = '';
    for (let t = 0; t < n; t++) {
        const off = (t - (n - 1) / 2) * 5;
        const c = [m[0] + u[0] * off, m[1] + u[1] * off];
        s += `<line x1="${f1(c[0] - nv[0] * 6)}" y1="${f1(c[1] - nv[1] * 6)}" x2="${f1(c[0] + nv[0] * 6)}" y2="${f1(c[1] + nv[1] * 6)}" stroke="#111827" stroke-width="2" data-code="tick" data-n="${n}"/>`;
    }
    return s;
};

const svgCarre = (S, u1, u2, nom) => {
    const k = 11; const a = unitaire(u1); const b = unitaire(u2);
    return `<polyline points="${f1(S[0] + a[0] * k)},${f1(S[1] + a[1] * k)} ${f1(S[0] + (a[0] + b[0]) * k)},${f1(S[1] + (a[1] + b[1]) * k)} ${f1(S[0] + b[0] * k)},${f1(S[1] + b[1] * k)}" fill="none" stroke="#111827" stroke-width="1.8" data-code="carre" data-sommet="${nom}"/>`;
};

const svgPoint = (P, nom, dir, dist = 15) => {
    const d = unitaire(dir);
    return `<circle cx="${f1(P[0])}" cy="${f1(P[1])}" r="2.4" fill="#1e293b"/>`
        + (nom ? `<text x="${f1(P[0] + d[0] * dist)}" y="${f1(P[1] + d[1] * dist + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${nom}</text>` : '');
};

/** Rend la scène ; data-figure = points nommés en coordonnées abstraites (6 décimales). */
export const svgTriangle = (scene, aria = 'Un triangle nommé.') => {
    const { points, segments = [], pointilles = [], angleDroit = null, triangle, ticks = [], cercle = null, sansEtiquette = [], cotes = [], angles = [], vertes = [], carres = [], codages = false, directions = {} } = scene;
    /** La direction imposée d'une étiquette, en pixels (y vers le bas), ou null. */
    const imposee = (n) => (directions[n] ? unitaire([directions[n][0], -directions[n][1]]) : null);
    const appuis = Object.values(points).slice();
    if (cercle) {
        const O = points[cercle.centre]; const r = norme(vec(O, points[cercle.par]));
        appuis.push([O[0] - r, O[1] - r], [O[0] + r, O[1] + r]);
    }
    const annote = cotes.length > 0 || angles.length > 0;
    const voisinsDe = (n) => triangle.filter((m) => m !== n);
    /** Les points en pixels pour une marge donnée, et de combien les écritures débordent du cadre. */
    const placer = (marge) => {
        const cadre = cadreDe(appuis, marge);
        const P = {}; Object.keys(points).forEach((n) => { P[n] = cadre.P(points[n]); });
        // le centre de gravité du triangle repousse les étiquettes vers l'extérieur
        const centre = triangle.reduce((acc, n) => [acc[0] + P[n][0] / triangle.length, acc[1] + P[n][1] / triangle.length], [0, 0]);
        if (!annote) return { px: P, G: centre, debord: 0 };
        // les écritures (cotes, mesures d'angle, noms des sommets) restent dans le cadre : sinon la marge grandit
        const boites = [
            ...cotes.map((c) => placeCote(P[c.de], P[c.a], centre, c.texte)),
            ...angles.map((a) => { const v = a.voisins || voisinsDe(a.sommet); return placeAngle(P[a.sommet], P[v[0]], P[v[1]], a.texte); }),
            ...Object.keys(points).map((n) => { const d = imposee(n) || unitaire(vec(centre, P[n])); return { c: [P[n][0] + d[0] * 15, P[n][1] + d[1] * 15], w: 11, h: HAUTEUR_TEXTE }; }),
        ];
        const debord = Math.max(0, ...boites.map((b) => Math.max(4 - (b.c[0] - b.w / 2), b.c[0] + b.w / 2 - (W - 4), 4 - (b.c[1] - b.h / 2), b.c[1] + b.h / 2 - (H - 4))));
        return { px: P, G: centre, debord };
    };
    let marge = MARGE; let place = placer(marge);
    for (let essai = 0; essai < 8 && place.debord > 0.5; essai++) { marge += place.debord + 1; place = placer(marge); }
    const { px, G } = place;
    let s = '';
    if (cercle) {
        const O = px[cercle.centre]; const r = norme(vec(O, px[cercle.par]));
        s += `<circle cx="${f1(O[0])}" cy="${f1(O[1])}" r="${f1(r)}" fill="none" stroke="#64748B" stroke-width="1.6" data-code="cercle" data-centre="${cercle.centre}"/>`;
    }
    segments.forEach(([a, b]) => { s += svgSegment(px[a], px[b]); });
    vertes.forEach(([a, b]) => { s += `<line x1="${f1(px[a][0])}" y1="${f1(px[a][1])}" x2="${f1(px[b][0])}" y2="${f1(px[b][1])}" stroke="#16A34A" stroke-width="2.4" data-code="parallele"/>`; });
    pointilles.forEach(([a, b]) => { s += svgSegment(px[a], px[b], true); });
    ticks.forEach((t) => { s += svgTicks(px[t.de], px[t.a], t.n); });
    if (angleDroit) {
        const voisins = triangle.filter((n) => n !== angleDroit);
        s += svgCarre(px[angleDroit], vec(px[angleDroit], px[voisins[0]]), vec(px[angleDroit], px[voisins[1]]), angleDroit);
    }
    carres.forEach((c) => { s += svgCarre(px[c.sommet], vec(px[c.sommet], px[c.voisins[0]]), vec(px[c.sommet], px[c.voisins[1]]), c.sommet); });
    const ecriture = (b, texte, cherche, attrs) => `<text x="${f1(b.c[0])}" y="${f1(b.c[1] + 4.5)}" font-size="13" font-weight="600" fill="${cherche ? COULEUR_CHERCHE : COULEUR_COTE}" text-anchor="middle" ${attrs}>${texte}</text>`;
    angles.forEach((a) => {
        const v = a.voisins || voisinsDe(a.sommet);
        const g = placeAngle(px[a.sommet], px[v[0]], px[v[1]], a.texte);
        s += `<path d="${g.arc}" fill="none" stroke="${a.cherche ? COULEUR_CHERCHE : '#111827'}" stroke-width="1.6" data-code="angle" data-sommet="${a.sommet}"/>`;
        if (a.texte) s += ecriture(g, a.texte, a.cherche, `data-angle="${a.sommet}"`);
    });
    cotes.forEach((c) => { s += ecriture(placeCote(px[c.de], px[c.a], G, c.texte), c.texte, c.cherche, `data-cote="${[c.de, c.a].sort().join('')}"`); });
    Object.keys(points).forEach((n) => {
        const P = px[n];
        let dir = vec(G, P);
        if (norme(dir) < 1) dir = [0, 1];
        // un point sur un côté (milieu, centre) : étiquette perpendiculaire au côté, à l'opposé du troisième sommet ;
        // un point intérieur (centre d'un triangle inscrit) : étiquette au-dessus
        if (!triangle.includes(n)) {
            const cotes = [[0, 1], [1, 2], [0, 2]].map(([i, j]) => [px[triangle[i]], px[triangle[j]], px[triangle[3 - i - j]]]);
            const sur = cotes.find(([X, Y]) => { const u = vec(X, Y); const w = vec(X, P); return Math.abs(u[0] * w[1] - u[1] * w[0]) / (norme(u) || 1) < 1.5; });
            if (sur) { const nv = perp(unitaire(vec(sur[0], sur[1]))); const versTiers = vec(P, sur[2]); dir = (nv[0] * versTiers[0] + nv[1] * versTiers[1] > 0) ? [-nv[0], -nv[1]] : nv; }
            else dir = [0, -1];
        }
        if (imposee(n)) dir = imposee(n);
        s += svgPoint(P, sansEtiquette.includes(n) ? '' : n, dir);
    });
    const dataFigure = Object.entries(points).map(([n, p]) => `${n}:${p[0].toFixed(6)},${p[1].toFixed(6)}`).join('|');
    const dataCotes = cotes.length ? ` data-cotes="${cotes.map((c) => `${[c.de, c.a].sort().join('')}:${c.texte}`).join('|')}"` : '';
    const dataAngles = angles.length ? ` data-angles="${angles.map((a) => `${a.sommet}:${a.texte}`).join('|')}"` : '';
    // codages : la liste des égalités codées, pour les solveurs (186 « Pourquoi sont-ils égaux ? ») ; seulement sur demande
    const tri = (p, q) => [p, q].sort().join('');
    const dataCodages = codages ? ` data-codages="${[
        ...ticks.map((t) => `s:${tri(t.de, t.a)}:${t.n}`),
        ...angles.map((a) => `a:${a.sommet}:${tri(...(a.voisins || voisinsDe(a.sommet)))}:${a.texte}`),
        ...carres.map((c) => `d:${c.sommet}:${tri(...c.voisins)}`),
    ].join('|')}"` : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-figure="${dataFigure}"${angleDroit ? ` data-angle-droit="${angleDroit}"` : ''}${dataCotes}${dataAngles}${dataCodages} role="img" aria-label="${aria}">${s}</svg>`;
};

/** Un triangle rectangle en `droit`, cathètes a et b (unités abstraites), tourné de `deg` (sept orientations). */
export const triangleRectangle = ({ lettres, droit = 0, a = 4, b = 3, deg = 0 }) => {
    const base = [[0, 0], [a, 0], [0, b]];
    const pts = tourner(base, deg);
    const points = {};
    const ordre = [droit, (droit + 1) % 3, (droit + 2) % 3];
    ordre.forEach((i, k) => { points[lettres[i]] = pts[k]; });
    return { points, triangle: [...lettres], segments: [[lettres[0], lettres[1]], [lettres[1], lettres[2]], [lettres[2], lettres[0]]], angleDroit: lettres[droit] };
};
export const ORIENTATIONS = [0, 50, 95, 140, 185, 230, 275];
